import { useState } from 'react';
import { Family, FamilyCreate, FamilyUpdate } from '../types/costwand.d';
import { useFamilies } from '../hooks/useFamilies.ts';
import FamilyForm from '../components/FamilyForm.tsx';
import FamilyTable from '../components/FamilyTable.tsx';
import { Button } from '../components/ui/button.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const FamiliesPage = () => {
  const {
    families,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createFamily,
    updateFamily,
    deleteFamily,
    fetchFamilies,
  } = useFamilies();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingFamily, setEditingFamily] = useState<Family | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const { whitelabelConfig } = useWhitelabelContext();


  const getEntityTitle = (defaultTitle: string, singular?: boolean) => 
    (singular && whitelabelConfig.entities?.['families'].title_singular) || whitelabelConfig.entities?.['families'].title || defaultTitle;

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddFamily = () => {
    setEditingFamily(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditFamily = (family: Family) => {
    setEditingFamily(family);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteFamily = async (id: number) => {
    await deleteFamily(id);
    fetchFamilies(currentPage);
  };

  const handleFormSubmit = async (data: FamilyCreate | FamilyUpdate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingFamily) {
        success = await updateFamily(editingFamily.id!, data as FamilyUpdate);
      } else {
        success = await createFamily(data as FamilyCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchFamilies(currentPage);
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingFamily(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">{getEntityTitle('Family', true)} Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddFamily}>Add New {getEntityTitle('Family', true)}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{(editingFamily ? 'Edit ' : 'Add New ') + getEntityTitle('Family', true)}</DialogTitle>
              <DialogDescription>
                {editingFamily ? 'Make changes to the ' + getEntityTitle('Family', true) + ' here.' : 'Add a new ' + getEntityTitle('Family', true) + ' to your list.'}
              </DialogDescription>
            </DialogHeader>
            <FamilyForm
              initialData={editingFamily}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              entityId="families"
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <FamilyTable
        families={families}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditFamily}
        onDelete={handleDeleteFamily}
        loading={loading}
        error={error}
        entityId="families"
        baseEndpoint="/families" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default FamiliesPage;